import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Edit, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { apiRequest } from "@/lib/queryClient";
import { EquipmentFormModal } from "./equipment-form-modal";

// ============================================================================
// Type definitions (camelCase — Drizzle converts snake_case automatically)
// ============================================================================
interface SecurityEquipment {
  id: string;
  securityId: string;
  equipmentType: string;
  serialNumber: string | null;
  assetTag: string | null;
  make: string | null;
  model: string | null;
  yearManufactured: number | null;
  description: string | null;
  condition: string | null;
  locationAddress: string | null;
  isMobile: boolean;
  hoursUsed: number | null;
  weightKg: string | null;
  capacity: string | null;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Constants
// ============================================================================

// Condition badge styling
const CONDITION_BADGE: Record<string, { label: string; className: string }> = {
  new: { label: "New", className: "bg-green-100 text-green-800" },
  excellent: { label: "Excellent", className: "bg-green-100 text-green-800" },
  good: { label: "Good", className: "bg-blue-100 text-blue-800" },
  fair: { label: "Fair", className: "bg-amber-100 text-amber-800" },
  poor: { label: "Poor", className: "bg-red-100 text-red-800" },
};

// Equipment type user-friendly labels
const EQUIPMENT_TYPE_LABELS: Record<string, string> = {
  forklift: "Forklift",
  access_equipment: "Access Equipment",
  agricultural_tractor: "Agricultural Tractor",
  agricultural_harvester: "Agricultural Harvester",
  agricultural_other: "Agricultural Other",
  construction_excavator: "Construction Excavator",
  construction_loader: "Construction Loader",
  construction_crane: "Construction Crane",
  construction_other: "Construction Other",
  manufacturing: "Manufacturing",
  medical_equipment: "Medical Equipment",
  it_equipment: "IT Equipment",
  office_equipment: "Office Equipment",
  other_equipment: "Other Equipment",
};

function getEquipmentTypeLabel(type: string): string {
  return EQUIPMENT_TYPE_LABELS[type] || type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

// ============================================================================
// Props
// ============================================================================
interface EquipmentDetailTabProps {
  securityId: string;
}

// ============================================================================
// Component
// ============================================================================
export function EquipmentDetailTab({ securityId }: EquipmentDetailTabProps) {
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch equipment data — 404 means no equipment recorded yet
  const { data: equipment, isLoading } = useQuery<SecurityEquipment | null>({
    queryKey: ["/api/crm/securities", securityId, "equipment"],
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/crm/securities/${securityId}/equipment`);
      if (response.status === 404) {
        return null;
      }
      if (!response.ok) {
        throw new Error("Failed to fetch equipment details");
      }
      return response.json();
    },
    enabled: !!securityId,
  });

  // ============================================================================
  // Loading state
  // ============================================================================
  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // ============================================================================
  // Empty state
  // ============================================================================
  if (!equipment) {
    return (
      <>
        <Card data-testid="equipment-detail-tab">
          <CardContent className="pt-6">
            <div className="text-center py-8">
              <p className="text-muted-foreground mb-4">No equipment details recorded yet.</p>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setModalOpen(true)}
                data-testid="equipment-add-button"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Equipment Details
              </Button>
            </div>
          </CardContent>
        </Card>

        <EquipmentFormModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          securityId={securityId}
          existingEquipment={null}
        />
      </>
    );
  }

  // ============================================================================
  // Data card
  // ============================================================================
  const conditionInfo = equipment.condition ? CONDITION_BADGE[equipment.condition] : null;

  return (
    <>
      <Card data-testid="equipment-detail-tab">
        <CardHeader className="flex flex-row items-center justify-between py-3">
          <CardTitle className="text-base font-medium">Equipment Details</CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setModalOpen(true)}
            data-testid="button-edit-equipment"
            aria-label="Edit Equipment"
          >
            <Edit className="h-4 w-4 mr-1" />
            Edit Equipment
          </Button>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            {/* Equipment Type */}
            <div>
              <p className="text-muted-foreground">Equipment Type</p>
              <p className="font-medium">{getEquipmentTypeLabel(equipment.equipmentType)}</p>
            </div>

            {/* Condition with coloured badge */}
            <div>
              <p className="text-muted-foreground">Condition</p>
              {conditionInfo ? (
                <Badge
                  variant="default"
                  className={conditionInfo.className}
                  data-testid="equipment-condition-badge"
                >
                  {conditionInfo.label}
                </Badge>
              ) : (
                <p className="font-medium">-</p>
              )}
            </div>

            {/* Mobile */}
            <div>
              <p className="text-muted-foreground">Mobile Equipment</p>
              <p className="font-medium">{equipment.isMobile ? "Yes" : "No"}</p>
            </div>

            {/* Make */}
            {equipment.make && (
              <div>
                <p className="text-muted-foreground">Make</p>
                <p className="font-medium">{equipment.make}</p>
              </div>
            )}

            {/* Model */}
            {equipment.model && (
              <div>
                <p className="text-muted-foreground">Model</p>
                <p className="font-medium">{equipment.model}</p>
              </div>
            )}

            {/* Year Manufactured */}
            {equipment.yearManufactured && (
              <div>
                <p className="text-muted-foreground">Year Manufactured</p>
                <p className="font-medium">{equipment.yearManufactured}</p>
              </div>
            )}

            {/* Serial Number */}
            {equipment.serialNumber && (
              <div>
                <p className="text-muted-foreground">Serial Number</p>
                <p className="font-medium">{equipment.serialNumber}</p>
              </div>
            )}

            {/* Asset Tag */}
            {equipment.assetTag && (
              <div>
                <p className="text-muted-foreground">Asset Tag</p>
                <p className="font-medium">{equipment.assetTag}</p>
              </div>
            )}

            {/* Hours Used */}
            {equipment.hoursUsed !== null && equipment.hoursUsed !== undefined && (
              <div>
                <p className="text-muted-foreground">Hours Used</p>
                <p className="font-medium">{equipment.hoursUsed.toLocaleString("en-AU")} hrs</p>
              </div>
            )}

            {/* Weight */}
            {equipment.weightKg && (
              <div>
                <p className="text-muted-foreground">Weight</p>
                <p className="font-medium">{equipment.weightKg} kg</p>
              </div>
            )}

            {/* Capacity */}
            {equipment.capacity && (
              <div>
                <p className="text-muted-foreground">Capacity</p>
                <p className="font-medium">{equipment.capacity}</p>
              </div>
            )}

            {/* Location Address */}
            {equipment.locationAddress && (
              <div className="col-span-2 md:col-span-3">
                <p className="text-muted-foreground">Location Address</p>
                <p className="font-medium">{equipment.locationAddress}</p>
              </div>
            )}

            {/* Description */}
            {equipment.description && (
              <div className="col-span-2 md:col-span-3">
                <p className="text-muted-foreground">Description</p>
                <p className="font-medium">{equipment.description}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <EquipmentFormModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        securityId={securityId}
        existingEquipment={equipment}
      />
    </>
  );
}
